import queryString from "query-string";
import { useEffect, useState } from "react";
import { getPathWithCountryCode } from "../utils/path";
import { PATHNAME_KEY } from "../constants/queryConstants";
import { removePLPFilterPrefix } from "./product-filters";
import type { Filter } from "@bmi-digital/components/filters";

export type URLFilter = {
  name: string;
  value: string[];
};

export type URLProductFilter = URLFilter;

export type ResultType =
  | "Simple"
  | "Simple Archive"
  | "Technical"
  | "Card Collection";

export type Source = "PIM" | "CMS" | "ALL";

export enum SourceEnum {
  PIM = "PIM",
  CMS = "CMS",
  ALL = "ALL"
}

export const convertToURLFilters = (
  filters: readonly Filter[]
): URLProductFilter[] => {
  return filters.reduce((carry: URLProductFilter[], { name, value }) => {
    if (value && value.length) {
      carry.push({
        name: removePLPFilterPrefix(name),
        value: Array.from(value)
      });
    }
    return carry;
  }, []);
};

export const sortAlphabeticallyBy =
  <T extends Record<string, unknown>>(propName: keyof T) =>
  (a: T, b: T) => {
    // eslint-disable-next-line security/detect-object-injection
    if (a[propName] < b[propName]) {
      return -1;
    }
    // eslint-disable-next-line security/detect-object-injection
    if (a[propName] > b[propName]) {
      return 1;
    }
    return 0;
  };

// eslint-disable-next-line security/detect-object-injection
export const updateFilterValue = (
  filters,
  filterName: string,
  filterValue: string,
  checked: boolean
) => {
  const addToArray = (array, value) => [...array, value];
  const removeFromArray = (array, value) => array.filter((v) => v !== value);
  const getNewValue = (filter, checked, value) => {
    return checked
      ? addToArray(filter.value || [], value)
      : removeFromArray(filter.value || [], value);
  };

  return filters.map((filter) => {
    return {
      ...filter,
      value:
        filter.name === filterName
          ? getNewValue(filter, checked, filterValue)
          : filter.value
    };
  });
};

export const clearFilterValues = (filters) => {
  return filters.map((filter) => ({
    ...filter,
    value: []
  }));
};

export const FILTER_KEY = "filters";
export const SEARCHTAB_KEY = "tab";

type FiltersLocation = { search: string; pathname: string };

export const getURLFilterValues = (): URLProductFilter[] => {
  const { search } = getWindowLocationFilters();
  const filters = queryString.parse(search)[FILTER_KEY.toString()];

  if (filters) {
    try {
      return JSON.parse(filters.toString());
    } catch (error) {
      // user may change url params so JSON.parse fails
      console.error("Filters can not be parsed");
    }
  }
  return [];
};

export const getUpdatedFilters = (filters: Filter[]): Filter[] => {
  const newFilterValues = getURLFilterValues();

  const updatedFilters = filters.map((filter) => {
    const currentQueryFilterValue = newFilterValues.find(
      ({ name }) => name === removePLPFilterPrefix(filter.name)
    )?.value;

    return {
      ...filter,
      value: ([] as string[])
        .concat(currentQueryFilterValue || [])
        .filter(Boolean)
    };
  });

  return updatedFilters;
};

export const setFiltersUrl = (newFilters: Filter[]): void => {
  const location = getWindowLocationFilters();
  const newUrlFilterValues = convertToURLFilters(newFilters);
  const newFilterNames = newFilters.map(({ name }) => name);

  const urlFilterValues = getURLFilterValues();

  newFilterNames.forEach((name) => {
    const index: number = urlFilterValues.findIndex(
      (filter) => filter.name === name
    );
    const value = newUrlFilterValues.find((value) => value.name === name)
      ?.value;

    if (index === -1) {
      urlFilterValues.push({ name, value: value || [] });
    } else {
      urlFilterValues[index as number] = { name, value: value || [] };
    }
  });

  const urlParams = urlFilterValues.filter(({ value }) =>
    Boolean(value?.length)
  );

  const params = queryString.parse(location.search);

  if (urlParams.length > 0) {
    params[FILTER_KEY as string] = JSON.stringify(urlParams);
  } else {
    delete params[FILTER_KEY as string];
  }

  replaceHistoryState({
    pathname: location.pathname,
    search: queryString.stringify(params)
  });
};

export const setSearchTabUrl = (tab: string): void => {
  const location = getWindowLocationFilters();
  const params = queryString.parse(location.search);

  params[SEARCHTAB_KEY as string] = tab;

  replaceHistoryState({
    pathname: location.pathname,
    search: queryString.stringify(params)
  });
};

export const getSearchTabUrl = (): string | null => {
  const { search } = getWindowLocationFilters();
  const searchtab = queryString.parse(search)[SEARCHTAB_KEY as string] ?? null;

  return Array.isArray(searchtab) ? searchtab[0] : searchtab;
};

export const REPLACE_STATE_EVENT = "onreplacestate";
const replaceHistoryState = ({
  pathname,
  search
}: {
  pathname: string;
  search: string;
}) => {
  const event = new CustomEvent(REPLACE_STATE_EVENT, {
    detail: {
      search
    }
  });
  typeof window !== "undefined" && window.dispatchEvent(event);
  history.replaceState(null, "", `${pathname}?${search}`);
};

export const useSearchParams = (): string => {
  const [params, setParams] = useState(getSearchParams());

  const onPopState = (e: CustomEvent) => {
    setParams(getSearchParams(e.detail.search));
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener(REPLACE_STATE_EVENT, onPopState);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener(REPLACE_STATE_EVENT, onPopState);
      }
    };
  }, []);

  return params;
};

export const getSearchParams = (search?: string) => {
  const params = queryString.parse(search || getWindowLocationFilters().search);

  params[PATHNAME_KEY.toString()] = location.pathname;

  return `?${queryString.stringify(params)}`;
};

export const getBackToResultsPath = (
  path: string,
  countryCode: string
): string => {
  const location = getWindowLocationFilters();
  const params = queryString.parse(location.search);

  if (params[PATHNAME_KEY.toString()]) {
    delete params[PATHNAME_KEY.toString()];
    return params && Object.keys(params).length > 0
      ? `${path}?${queryString.stringify(params)}`
      : `${path}`;
  }
  return `${getPathWithCountryCode(countryCode, "search")}${location.search}`;
};

export const getWindowLocationFilters = (): FiltersLocation =>
  typeof window !== "undefined"
    ? window.location
    : { search: "", pathname: "" };

export const QUERY_FILTER_DELIMITER = "$";

export const replaceDotFiltersParameter = (filtersFromQuery: string) =>
  filtersFromQuery.replace(/\./g, QUERY_FILTER_DELIMITER);
